import { MaterialGroup } from '@cutr/constants/cutlist';
import { useFlag } from '@unleash/proxy-client-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  activeGroupSelector,
  useActiveGroup,
  useActiveGroupGrainDirection,
  useEdgebandSelected,
  useMaterialGroupState,
} from '@/api/materialsGroup';
import { selectActions, useCutlistState } from '@/api/store';
import { EdgesTooltip } from '@/blocks/EdgesTooltip';
import { useBeamSizes } from '@/hooks';
import { Icon, Info } from '@/primitives/Icons';
import { useCurrentFeatures } from '@/theme';

import { ActiveGroupPartErrors, Errors } from '../Errors';
import { EmptyTableNudge } from '../PartTableCommon/EmptyTableNudge';
import { MaterialGroupActions } from '../PartTableCommon/MaterialGroupActions';
import styles from '../PartTableCommon/styles.module.css';
import { useTableErrors } from '../PartTableCommon/useTableErrors';
import { BeamGroupHeader, MaterialGroupHeader } from './MaterialGroupHeader';
import { MaterialGroupNotes } from './MaterialGroupNotes';
import { TableRow } from './TableRow';

export const PartTable = ({ disabled = false }: { disabled?: boolean }) => {
  return (
    <form className={styles.orderSection} name="parts">
      <fieldset
        disabled={disabled}
        style={{ border: 0, padding: 0, margin: 0, minInlineSize: 'auto' }}
      >
        <Table />
      </fieldset>
    </form>
  );
};

type TableHeader = {
  labelKey: string;
  id?: string;
  width?: string;
  colSpan?: number;
  tooltipKey?: string;
};

const tableHeaders: TableHeader[] = [
  { labelKey: 'field.partName.label', id: 'label', width: '80px' },
  { labelKey: 'field.quantity.label' },
  { labelKey: 'field.length.label' },
  { labelKey: 'field.width.label' },
  { labelKey: 'field.edgeband.label', id: 'edgebanding', colSpan: 5 },
  { labelKey: 'field.edgeProfile.label', id: 'edgeProfile', colSpan: 5 },
  {
    labelKey: 'field.grainDirection.label',
    id: 'grain',
    tooltipKey: 'field.grainDirection.tooltip',
  },
  { labelKey: 'field.aiSuggestions.label', id: 'aiSuggestions' },
  { labelKey: 'field.labeling', id: 'createLabel' },
  { labelKey: '', width: '50px' },
];

const beamTableHeaders: TableHeader[] = [
  { labelKey: 'field.partName.label', id: 'label', width: '80px' },
  { labelKey: 'field.quantity.label' },
  { labelKey: 'field.profileThickness.label', id: 'profileThickness' },
  { labelKey: 'field.profileWidth.label', id: 'profileWidth' },
  { labelKey: 'field.length.label' },
  {
    labelKey: 'field.roundedEdge.label',
    tooltipKey: 'field.roundedEdge.tooltip',
  },
  { labelKey: '', width: '50px' },
];

const useHeaders = (): TableHeader[] => {
  const activeGroup = useMaterialGroupState(activeGroupSelector);
  const { hasEdgeProfiling, hasCreateLabels, llmFileImport } =
    useCurrentFeatures();
  const isCustomerExperimentEnabled = useFlag('customer-llm-file-import');
  const hasGrainDirection = useActiveGroupGrainDirection();

  if (activeGroup?.type === 'beams') {
    return beamTableHeaders;
  }

  return tableHeaders.filter(({ id }) => {
    if (id === 'edgebanding') return Boolean(activeGroup?.edgeband);
    if (id === 'edgeProfile') return hasEdgeProfiling;
    if (id === 'grain') return hasGrainDirection;
    if (id === 'aiSuggestions')
      return (
        llmFileImport &&
        isCustomerExperimentEnabled &&
        activeGroup?.type !== 'sheets-only'
      );
    if (id === 'createLabel') return hasCreateLabels === 'part';
    return true;
  });
};

export const SkeletonTable = () => {
  const groups = useMaterialGroupState((state) => state.groups);
  const headers = useHeaders();
  return (
    <form className={styles.orderSection} name="parts">
      {groups.map((g) => (
        <section key={g.id} className={styles.partSection}>
          <table>
            <Head headers={headers} />
            <SkeletonBody headers={headers} />
          </table>
        </section>
      ))}
    </form>
  );
};

const Table = () => {
  const groups = useMaterialGroupState((state) => state.groups);
  const activeTab = useMaterialGroupState((state) => state.activeGroup);
  const activeGroup = useActiveGroup();
  const headers = useHeaders();
  useTableErrors();

  const shouldShowPartTable =
    Boolean(activeGroup?.core1) || Boolean(activeGroup?.aiSuggestions?.core1);

  return (
    <>
      {groups
        .filter((g) => !activeTab || g.id === activeTab)
        .map((g) => {
          return (
            <section
              key={g.id}
              className={styles.partSection}
              id={`section-${g.id}`}
            >
              {g.type === 'beams' ? (
                <BeamGroupHeader group={g} />
              ) : (
                <MaterialGroupHeader group={g} />
              )}

              {(activeGroup?.type === 'beams' ||
                (activeGroup?.type && shouldShowPartTable)) && (
                <>
                  <ActiveGroupPartErrors />
                  <table>
                    <Head headers={headers} />
                    <Body group={g} />
                  </table>

                  <EmptyTableNudge group={g} />
                  <MaterialGroupActions group={g} />
                  <Errors />

                  <MaterialGroupNotes group={g} />
                </>
              )}
            </section>
          );
        })}
    </>
  );
};

const Head = ({ headers }: { headers: TableHeader[] }) => {
  const { edgebandingUI, hasEdgeProfiling, hasCreateLabels } =
    useCurrentFeatures();
  const { t } = useTranslation(undefined, { keyPrefix: 'cutlist-form' });
  const isEdgebandSelected = useEdgebandSelected();
  const hasGrainDirection = useActiveGroupGrainDirection();
  const activeGroup = useActiveGroup();
  const hasEdgebanding = isEdgebandSelected && edgebandingUI;
  const beamSizes = useBeamSizes(activeGroup?.type === 'beams');

  const getBeamSizesTooltip = (id?: string) => {
    if (!beamSizes.length) return;
    if (id === 'profileThickness') {
      return t('beamThicknessTooltip', {
        thicknessesMM: beamSizes.map((s) => s.thicknessMM).join(', '),
      });
    }
    if (id === 'profileWidth') {
      return [
        t('beamWidthTooltip'),
        ...beamSizes.map(({ thicknessMM, widthsMM }) =>
          t('beamSizesRow', { thicknessMM, widthsMM: widthsMM.join(', ') })
        ),
      ].join('\n');
    }
  };

  return (
    <thead>
      <tr>
        {headers.map((h, i) => {
          const edgesHeader = (
            <th
              key={h.labelKey || i}
              style={{ minWidth: h.width }}
              colSpan={h.colSpan}
            >
              <div>
                {t(h.labelKey)}
                <EdgesTooltip />
              </div>
            </th>
          );

          switch (h.id) {
            case 'edgebanding':
              if (!hasEdgebanding) return null;
              return edgesHeader;
            case 'edgeProfile':
              if (!hasEdgeProfiling) return null;
              return edgesHeader;
          }

          const beamSizesTooltip = getBeamSizesTooltip(h.id);

          return (
            <th
              key={h.labelKey || i}
              style={{ minWidth: h.width }}
              colSpan={h.colSpan || 1}
            >
              <div>
                {h.labelKey && t(h.labelKey)}
                {h.tooltipKey && (
                  <Icon
                    icon={<Info />}
                    color="var(--gray-7)"
                    className="tooltip"
                    data-tooltip={t(h.tooltipKey)}
                  />
                )}
                {beamSizesTooltip && (
                  <Icon
                    icon={<Info />}
                    color="var(--gray-7)"
                    className="tooltip tooltipMultiline"
                    data-tooltip={beamSizesTooltip}
                  />
                )}
              </div>
            </th>
          );
        })}
      </tr>
      {(hasEdgebanding || hasEdgeProfiling) &&
        activeGroup?.type !== 'beams' && (
          <tr>
            <th />
            <th />
            <th />
            <th />
            <th>{t('field.edgebanding.sides.l1')}</th>
            <th>{t('field.edgebanding.sides.l2')}</th>
            <th>{t('field.edgebanding.sides.w1')}</th>
            <th>{t('field.edgebanding.sides.w2')}</th>
            <th />
            {hasGrainDirection && <th />}
            {headers.find((h) => h.id === 'aiSuggestions') && <th />}
            {hasCreateLabels === 'part' && <th />}
            <th />
          </tr>
        )}
    </thead>
  );
};

const SkeletonBody = ({ headers }: { headers: TableHeader[] }) => {
  const parts = [{ id: 1 }, { id: 2 }, { id: 3 }];

  return (
    <tbody>
      {parts.map((part) => {
        return <SkeletonRow headers={headers} key={part.id} />;
      })}
    </tbody>
  );
};

const SkeletonRow = ({ headers }: { headers: TableHeader[] }) => (
  <tr>
    {headers.map((h) => {
      return (
        <td
          key={h.labelKey}
          className={styles.loading}
          colSpan={h.colSpan ?? 1}
        >
          <div className={styles.bar}></div>
        </td>
      );
    })}
  </tr>
);

const Body = ({ group }: { group: MaterialGroup }) => {
  const ref = React.useRef(null);
  const shouldFocusLast = React.useRef(false);
  const { addPart } = useCutlistState(selectActions);

  const parts = useCutlistState((state) =>
    state.parts.filter((part) => part.groupId === group.id)
  );

  React.useEffect(() => {
    if (!ref.current) return;
    const element = ref.current as HTMLElement;

    const onFocusOut = (e: FocusEvent) => {
      if (element.contains(e.relatedTarget as HTMLElement)) return;
      const time = e.timeStamp;
      window.addEventListener(
        'keyup',
        (e) => {
          if (e.key === 'Tab' && e.timeStamp - time < 200) {
            addPart(group);
            shouldFocusLast.current = true;
          }
        },
        { once: true }
      );
    };
    element.addEventListener('focusout', onFocusOut);
    return () => {
      element.removeEventListener('focusout', onFocusOut);
    };
  }, [ref, addPart, group]);

  React.useEffect(() => {
    if (!ref.current) return;
    if (!shouldFocusLast.current) return;

    const element = ref.current as HTMLElement;
    const lastInput = element.querySelector(
      'tr:last-child input:first-of-type'
    ) as HTMLInputElement;
    lastInput?.focus();
    shouldFocusLast.current = false;
  }, [parts, ref, shouldFocusLast]);

  return (
    <tbody ref={ref}>
      {parts.map((part) => {
        return <TableRow part={part} key={part.id} />;
      })}
    </tbody>
  );
};
